import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Mail, Phone } from "lucide-react";
import { isValidTel } from "../../Components/Common/PhoneInput/phoneNumbers";
import firebase from "../../auth/firebase";
import { signIn, verificationCode } from "../../auth/utils";
import { isMobileSignInLink } from "../../auth/mobileSignInLink";
import MobileSignInFallback from "./MobileSignInFallback";
import { startRegisteredMobileLogin } from "../../auth/mobileLogin";
import { hasFirebaseAction } from "../../auth/consoleSession";
import { useAuthContext } from "../../context/AuthContext";
import { useTranslation } from "../../context/Internationalization";
import { LOGIN_ROUTE, REGISTRATION_ROUTE } from "../../routes";
import { readRegistrationProgress } from "../Registration/registrationProgress";
import { SessionAccessDenied, SessionError } from "../../Components/SessionBoundary";
import Loader from "../../Components/Loader";
import AuthLayout from "./AuthLayout";
import AuthTextField from './AuthTextField';
import AuthPhoneField from './AuthPhoneField';
import LoadingButton from "../../Components/Common/LoadingButton";
import StatusAlert from "../../Components/Common/StatusAlert";
import { Button } from "../../shadcn/components/ui/button";
import { Checkbox } from "../../shadcn/components/ui/checkbox";
import { Field, FieldError, FieldLabel, FieldSeparator } from "../../shadcn/components/ui/field";
import useEmailCodeSignIn from './useEmailCodeSignIn';
import EmailCodeInput from './EmailCodeInput';
import appStoreBadge from "../../assets/images/store-badges/app-store.svg";
import googlePlayBadge from "../../assets/images/store-badges/google-play.svg";

const APP_STORE_DOWNLOAD_LINK = "https://apps.apple.com/gr/app/playerdex-academy/id1584881110";
const GOOGLE_PLAY_DOWNLOAD_LINK = "https://play.google.com/store/apps/details?id=app.playerdex.onetouchfootball";
const emailCodeFieldErrors = new Set(['email_code_invalid', 'email_code_expired', 'email_code_attempts_exhausted']);

const LoginForm = () => {
  const auth = useAuthContext();
  const { t, lang } = useTranslation();
  const [method, setMethod] = useState("email");
  const emailSignIn = useEmailCodeSignIn(auth, lang);
  const { email, setEmail, challenge, resendIn } = emailSignIn;
  const checkEmail = Boolean(challenge);
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [rememberPhone, setRememberPhone] = useState(() => Boolean(localStorage.getItem("rememberMe")));
  const [phoneBusy, setBusy] = useState(false);
  const busy = phoneBusy || emailSignIn.busy;
  const [error, setError] = useState("");
  const submitPending = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("error")) {
      setError(params.get("error") === "login_session_expired" ? "login_session_expired" : "invalid_or_expired_login_link");
      window.history.replaceState(null, "", LOGIN_ROUTE);
    }
  }, []);

  const submitPhone = async (event) => {
    event.preventDefault();
    if (submitPending.current) return;
    if (!confirmation && !isValidTel(mobile)) {
      setError("error_invalid_phone_number"); return;
    }
    submitPending.current = true;
    setBusy(true);
    setError("");
    try {
      if (!confirmation) {
        if (rememberPhone) localStorage.setItem("rememberMe", "1");
        else localStorage.removeItem("rememberMe");
        const result = await startRegisteredMobileLogin(mobile.replace(/ /g, "").trim(), signIn);
        if (!result.isRegistered) {
          setMethod("email");
          emailSignIn.changeEmail();
          setError("mobile_not_registered_use_email");
          return;
        }
        setConfirmation(result.confirmationResult);
      } else {
        const token = await verificationCode(confirmation, code);
        const identity = firebase.auth().currentUser;
        const result = await auth.refreshSession({ firebaseUser: identity, token });
        if (!result.user.email) {
          const storage = localStorage.getItem("rememberMe") ? localStorage : sessionStorage;
          storage.setItem("lastLoginMethod", "sms");
        }
      }
    } catch (_) {
      if (mounted.current) setError(confirmation ? "registration_sms_code_failed" : "registration_sms_send_failed");
    } finally {
      submitPending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  if (auth.sessionStatus === "loading") return <Loader fullPage message={t("signing_in")} />;
  if (auth.sessionStatus === "error") return <SessionError />;
  if (auth.sessionStatus === "ready" && !hasFirebaseAction()) {
    if (auth.session?.kind === "restricted") return <SessionAccessDenied />;
    const pending = !auth.authState?.impersonate?._id && readRegistrationProgress(auth.firebaseUser?.uid);
    return <Navigate to={pending ? REGISTRATION_ROUTE : auth.session.destination} replace />;
  }

  const emailError = emailSignIn.error || (checkEmail && !emailSignIn.expiresIn ? 'email_code_expired' : '');
  const visibleError = error || (method === 'email' ? emailError : '');
  const emailFieldError = method === 'email' && !checkEmail && visibleError === 'invalid_email' ? visibleError : '';
  const emailCodeFieldError = method === 'email' && checkEmail && emailCodeFieldErrors.has(visibleError) ? visibleError : '';
  const phoneFieldError = method === 'phone' && !confirmation && visibleError === 'error_invalid_phone_number' ? visibleError : '';
  const smsCodeFieldError = method === 'phone' && confirmation && visibleError === 'registration_sms_code_failed' ? visibleError : '';
  const formError = emailFieldError || emailCodeFieldError || phoneFieldError || smsCodeFieldError ? '' : visibleError;

  return (
    <AuthLayout>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="mb-2">
            {t(method === "phone" ? "login_with_phone" : checkEmail ? "registration_check_email" : "registration_welcome")}
          </h1>
          <p id="email-code-help" className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
            {method === 'email' && checkEmail ? <>{t('email_code_sent_to')}{' '}<strong>{email}</strong>.{' '}{t('email_code_expiry_help')}</> :
              t(method === "phone" ? "registration_phone_description" : "registration_email_description")}
          </p>
        </div>
        {formError && <StatusAlert id="login-error" severity="error">{t(formError)}</StatusAlert>}
        <div id="recaptcha-container" />
        {method === "email" && (checkEmail ? <form onSubmit={(event) => { setError(''); emailSignIn.verify(event); }} className="flex flex-col gap-6" noValidate>
          <div>
            <div className="flex justify-center py-2">
              <EmailCodeInput value={emailSignIn.code} onChange={(value) => {
                emailSignIn.setCode(value);
                if (emailCodeFieldErrors.has(emailSignIn.error)) emailSignIn.setError('');
              }} disabled={busy || !emailSignIn.expiresIn}
                label={t('email_code_label')} error={Boolean(emailCodeFieldError)}
                descriptionId={emailCodeFieldError ? 'email-code-error email-code-help' : 'email-code-help'} />
            </div>
            {emailCodeFieldError && <FieldError id="email-code-error" className="mt-2 px-3">
              {t(emailCodeFieldError)}
            </FieldError>}
          </div>
          <LoadingButton size="lg" type="submit" loading={busy} disabled={emailSignIn.code.length !== 6 || !emailSignIn.expiresIn}>
            {t('email_code_verify')}
          </LoadingButton>
          <p className="text-center text-sm">{t('email_code_not_received')}{' '}
            {resendIn > 0 ? <span className="text-muted-foreground">
              {`${t('email_code_resend_in')} ${Math.floor(resendIn / 60)}:${String(resendIn % 60).padStart(2, '0')}`}
            </span> : <Button type="button" variant="link" disabled={busy} onClick={(event) => { setError(''); emailSignIn.send(event); }}
              className="h-auto p-0 align-baseline text-foreground underline">
              {t('email_code_resend')}
            </Button>}
          </p>
          <Button variant="outline" size="lg" type="button" disabled={busy} onClick={() => { setError(''); emailSignIn.changeEmail(); }}>{t("registration_change_email")}</Button>
        </form> : <form onSubmit={(event) => { setError(''); emailSignIn.send(event); }} className="flex flex-col gap-4" noValidate>
          <AuthTextField autoFocus hideLabel label={t("email")} name="email" type="email" required
            autoComplete="email" value={email} disabled={busy} onChange={(event) => {
              setEmail(event.target.value);
              if (emailSignIn.error === 'invalid_email') emailSignIn.setError('');
            }} error={Boolean(emailFieldError)} helperText={emailFieldError ? t(emailFieldError) : ''} />
          <LoadingButton size="lg" loading={busy} disabled={resendIn > 0} type="submit">{t("registration_continue")}</LoadingButton>
        </form>)}
        {method === "phone" && <form onSubmit={submitPhone} className="flex flex-col gap-4" noValidate>
          {!confirmation ? <>
            <AuthPhoneField label={t("mobile")} defaultCountry="GR" onlyCountries={["GR", "CY"]}
              value={mobile} onChange={(value) => { setMobile(value); if (phoneFieldError) setError(''); }} disabled={busy}
              error={Boolean(phoneFieldError)} helperText={phoneFieldError ? t(phoneFieldError) : ''}
              inputProps={{ autoComplete: "tel", name: "mobile" }} />
            <Field orientation="horizontal" className="items-center">
              <Checkbox id="remember-phone" checked={rememberPhone} disabled={busy}
                onCheckedChange={(checked) => setRememberPhone(checked)} />
              <FieldLabel htmlFor="remember-phone" className="flex-none font-normal">{t("remember_me")}</FieldLabel>
            </Field>
          </> : <AuthTextField autoFocus label={t("insert_code")} value={code} disabled={busy}
            error={Boolean(smsCodeFieldError)} helperText={smsCodeFieldError ? t(smsCodeFieldError) : ''}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
              if (smsCodeFieldError) setError('');
            }}
            inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6 }} />}
          <LoadingButton size="lg" type="submit" loading={busy} disabled={Boolean(confirmation && code.length !== 6)}>
            {t(confirmation ? "registration_verify_sms" : "registration_send_sms")}
          </LoadingButton>
        </form>}
        {!(method === 'email' && checkEmail) && <>
        <Button variant="outline" size="lg" type="button" disabled={busy}
          onClick={() => {
          setError("");
          emailSignIn.setError('');
          if (method === "phone") {
            setConfirmation(null);
            setCode("");
          }
          setMethod(method === "email" ? "phone" : "email");
        }}>{method === "email" ? <Phone aria-hidden="true" /> : <Mail aria-hidden="true" />}{t(method === "email" ? "login_with_phone" : "login_with_email")}</Button>
        <FieldSeparator>{t("registration_or")}</FieldSeparator>
        <p className="text-center text-muted-foreground">{t("registration_mobile_app")}</p>
        <div className="flex justify-center gap-2">
          <a href={APP_STORE_DOWNLOAD_LINK} target="_blank" rel="noopener noreferrer" aria-label={t("download_on_app_store")}>
            <img src={appStoreBadge} alt="" className="block h-10 w-[135px]" />
          </a>
          <a href={GOOGLE_PLAY_DOWNLOAD_LINK} target="_blank" rel="noopener noreferrer" aria-label={t("get_it_on_google_play")}>
            <img src={googlePlayBadge} alt="" className="block h-10 w-[135px]" />
          </a>
        </div>
        </>}
      </div>
    </AuthLayout>
  );
};
// Also protect direct mounts of Login outside the application's outer boundary.
export const Login = () => isMobileSignInLink()
  ? <MobileSignInFallback /> : <LoginForm />;
export default Login;
